/**
 * SvlanChecker – reusable AJAX SVLAN availability checker.
 *
 * Usage:
 *   import SvlanChecker from 'shared/SvlanChecker';
 *
 *   this.svlanChecker = new SvlanChecker({
 *     checkUrl:       $('#svlan').data('svlan-check-url'),
 *     submitSelector: '#order-form-submit',
 *   });
 *
 *   // Clean up when the page controller is destroyed:
 *   this.svlanChecker.destroy();
 *
 * The check is skipped when:
 *   - the SVLAN field is hidden (no PDC order selected)
 *   - the SVLAN value is empty (server will auto-assign)
 *   - the value is out of the [2–4091] range (handled by existing range validation)
 */
export default class SvlanChecker {
  /**
   * @param {object} opts
   * @param {string}  opts.checkUrl       – backend endpoint URL
   * @param {string}  opts.submitSelector – CSS selector for the submit button(s)
   * @param {string} [opts.pdcSelector]   – CSS selector for the PDC order select (default: '.js-pdc-select')
   * @param {number} [opts.debounce]      – debounce delay in ms (default: 600)
   */
  constructor({ checkUrl, submitSelector, pdcSelector = '.js-pdc-select', debounce = 600 }) {
    this.checkUrl       = checkUrl;
    this.submitSelector = submitSelector;
    this.pdcSelector    = pdcSelector;
    this.debounce       = debounce;

    this.$svlanInput = $('#svlan');
    this.$svlanGroup = $('#svlan-group');
    this.$svlanError = $('#svlan-error');

    this._timer          = null;
    this._available       = true;  // optimistic default
    this._checking        = false; // true while AJAX/debounce is in-flight
    this._lastCheckedSvlan = null; // svlan value of the last completed check
    this._lastCheckedPdc   = null; // PDC order id of the last completed check

    if (!this.checkUrl || this.$svlanInput.length === 0) return;

    this._bindEvents();
  }

  // ── Public ────────────────────────────────────────────────────────────────

  /** Returns true when the field is either hidden, empty, or the last check passed.
   *  Stays true while a check is in-flight so navigation isn't blocked unnecessarily;
   *  the submit button is separately disabled during that window.
   */
  get isAvailable() {
    if (!this._isFieldVisible()) return true;
    if (!this.$svlanInput.val()) return true;
    // Only block if we have a *confirmed* negative – not while still checking
    return this._available;
  }

  /** True while the debounce timer or AJAX request is in-flight. */
  get isChecking() {
    return this._checking;
  }

  destroy() {
    clearTimeout(this._timer);
    this.$svlanInput.off('input.svlanChecker change.svlanChecker');
    $(document).off('change.svlanChecker', this.pdcSelector);
  }

  // ── Private ───────────────────────────────────────────────────────────────

  _bindEvents() {
    this.$svlanInput.on('input.svlanChecker change.svlanChecker', () => this._onSvlanChange());
    $(document).on('change.svlanChecker', this.pdcSelector, () => this._onPdcChange());
  }

  _onPdcChange() {
    clearTimeout(this._timer);
    this._checking        = false;
    this._lastCheckedSvlan = null;
    this._lastCheckedPdc   = null;
    this._clearStatus();
    this._setAvailable(true);
  }

  _onSvlanChange() {
    clearTimeout(this._timer);

    const raw = this.$svlanInput.val();

    if (!raw) {
      this._checking = false;
      this._clearStatus();
      this._setAvailable(true);
      return;
    }

    const num = parseInt(raw, 10);
    if (isNaN(num) || num < 2 || num > 4091) {
      // Out-of-range – confirmed invalid; block submit but don't set _checking.
      this._checking = false;
      this._lastCheckedSvlan = null;
      this._setAvailable(false);
      return;
    }

    // The browser fires a `change` event on number inputs when they lose focus
    // (e.g. the user clicks the submit button). If the value and PDC haven't
    // changed since the last completed check we already have the result – skip
    // the AJAX call so the submit click isn't blocked by a redundant re-check.
    const pdcOrderId = $(this.pdcSelector).val();
    if (raw === this._lastCheckedSvlan && pdcOrderId === this._lastCheckedPdc) {
      return;
    }

    this._showChecking(); // sets _checking = true, disables submit
    this._timer = setTimeout(() => this._doCheck(), this.debounce);
  }

  _doCheck() {
    const svlan      = this.$svlanInput.val();
    const pdcOrderId = $(this.pdcSelector).val();

    if (!svlan || !pdcOrderId) {
      this._clearStatus();
      this._setAvailable(true);
      return;
    }

    $.ajax({
      url:      this.checkUrl,
      method:   'GET',
      dataType: 'json',
      data:     { svlan, pdc_order_id: pdcOrderId },
      success: (data) => {
        this._checking        = false;
        this._lastCheckedSvlan = svlan;
        this._lastCheckedPdc   = pdcOrderId;
        if (data.available) {
          this._showAvailable();
          this._setAvailable(true);
        } else {
          this._showUnavailable(data.message);
          this._setAvailable(false);
        }
      },
      error: () => {
        // On network error – clear status, don't block the form.
        this._checking        = false;
        this._lastCheckedSvlan = null; // don't cache on error
        this._clearStatus();
        this._setAvailable(true);
      },
    });
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  _showChecking() {
    this._checking = true;
    this._available = true; // remain optimistic until confirmed otherwise
    this.$svlanInput.removeClass('is-valid is-invalid');
    this.$svlanError.hide();
    this._statusEl()
      .html('<span class="spinner-border spinner-border-sm mr-1" role="status"></span> Vérification…')
      .show();
    // Disable submit while checking, but don't touch _available
    $(this.submitSelector)
      .prop('disabled', this._isFieldVisible())
      .toggleClass('disabled', this._isFieldVisible())
      .attr('title', this._isFieldVisible() ? 'Vérification du SVLAN en cours…' : null);
  }

  _showAvailable() {
    this.$svlanInput.removeClass('is-invalid').addClass('is-valid');
    this.$svlanError.hide();
    this._statusEl()
      .html('<span class="text-success"><i class="mdi mdi-check-circle"></i> SVLAN disponible</span>')
      .show();
  }

  _showUnavailable(message) {
    this.$svlanInput.addClass('is-invalid').removeClass('is-valid');
    this.$svlanError
      .text(message || 'Ce SVLAN est déjà utilisé pour cette porte de collecte.')
      .show();
    this._statusEl().hide();
  }

  _clearStatus() {
    this.$svlanInput.removeClass('is-valid is-invalid');
    this.$svlanError.hide();
    this._statusEl().hide();
  }

  _statusEl() {
    let $el = $('#svlan-availability-status');
    if ($el.length === 0) {
      $el = $('<div id="svlan-availability-status" class="small mt-1"></div>').hide();
      this.$svlanInput.after($el);
    }
    return $el;
  }

  _isFieldVisible() {
    return this.$svlanGroup.length > 0 && this.$svlanGroup.is(':visible');
  }

  _setAvailable(available) {
    this._available = available;

    const block = this._isFieldVisible() && !available;
    $(this.submitSelector)
      .prop('disabled', block)
      .toggleClass('disabled', block)
      .attr('title', block ? 'Résolvez le conflit SVLAN avant de soumettre.' : null);
  }
}
